import { resolveSafeAssetBlob } from './loadImageSafely';
import { decodeTo16kMono } from './decodeAudioTo16kMono';

// On-device speech-to-text for "Include recording as text" — see
// workers/transcribeAudio.worker.ts for why this runs in a Worker and why
// whisper-tiny.en specifically. This file is the main-thread half: it owns
// the singleton worker, resolves R2 audio through the same CORS-safe proxy
// path photos already use, decodes it to a raw waveform (see
// decodeAudioTo16kMono.ts for why that must happen here, not in the worker),
// and turns the worker's message events into a plain Promise-based API.

type WorkerMessage =
  | { type: 'result'; requestId: string; text: string }
  | { type: 'ready'; requestId: string }
  | { type: 'error'; requestId: string; message: string };

let worker: Worker | null = null;
function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('../workers/transcribeAudio.worker.ts', import.meta.url), { type: 'module' });
  }
  return worker;
}

function nextRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function waitForResponse(requestId: string): Promise<WorkerMessage> {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    function onMessage(event: MessageEvent<WorkerMessage>) {
      if (event.data.requestId !== requestId) return;
      w.removeEventListener('message', onMessage);
      w.removeEventListener('error', onError);
      resolve(event.data);
    }
    function onError(event: ErrorEvent) {
      w.removeEventListener('message', onMessage);
      w.removeEventListener('error', onError);
      reject(new Error(event.message || 'Transcription worker error'));
    }
    w.addEventListener('message', onMessage);
    w.addEventListener('error', onError);
  });
}

/** Fire-and-forget model warm-up — call when the "Include recording as text" modal is first opened, not on app start, so the ~40MB model download only happens if the feature is actually used. */
export function preloadTranscriptionModel(): void {
  const requestId = nextRequestId();
  getWorker().postMessage({ type: 'warmup', requestId });
  waitForResponse(requestId).catch(() => { /* best-effort — a real transcribe attempt will surface the error */ });
}

/**
 * Transcribes a voice recording (an EventAudio's filePath, same shape as a
 * photo's) into plain text. Always returns a draft to review/edit — never
 * treat this as a final transcript; whisper-tiny.en's accuracy is genuinely
 * modest on real family recordings (background noise, older/quieter voices).
 */
export async function transcribeAudioClip(audioUrl: string): Promise<string> {
  const blob = await resolveSafeAssetBlob(audioUrl);
  const audioData = await decodeTo16kMono(blob);
  const requestId = nextRequestId();
  const responsePromise = waitForResponse(requestId);
  // Transfer the underlying buffer instead of structured-cloning it — free or
  // near-free for the couple of MB a short recording decodes to.
  getWorker().postMessage({ type: 'transcribe', requestId, audioData }, [audioData.buffer]);
  const response = await responsePromise;
  if (response.type === 'error') throw new Error(response.message);
  if (response.type !== 'result') throw new Error('Unexpected transcription response');
  return response.text;
}
